import { z } from 'zod';
import { Cents, PositionKey, Price, PricePeriod } from './catalog-prices.js';
import { EmailProtection, ENGAGEMENT_OPTIONS, PostMode, ReceiptChannel, SepaScheme, } from './engagement-choices.js';
import { standardOf, } from './draft-questions.js';
import { LegalForm } from './firm.js';
import { BookkeepingModel, BookkeepingPackage, CatalogArea } from './service-catalog.js';
import { isoDay } from './values.js';
/**
 * The tax-advisory contract of a client (ADR-071 § 6, ADR-072): an engagement per client and
 * contracting party, and its versions - a draft the firm edits, then an offer frozen with its
 * documents. What a version holds is the firm's choice from its catalogue; whether it may be
 * offered, the rule pack `stb-vertrag` says.
 */
/**
 * The states of an engagement and of each of its versions (STBV-E B.2). While a version is in
 * force or terminated, the engagement keeps that state; an addendum goes through its own states
 * beside it and, once in force, supersedes the version it supplements.
 */
export const EngagementStatus = z
    .enum([
    'draft',
    'offered',
    'accepted',
    'declined',
    'expired',
    'withdrawn',
    'in_force',
    'revoked',
    'superseded',
    'terminated',
    'ended',
])
    .meta({ id: 'EngagementStatus' });
export const engagementStatusLabel = {
    draft: 'Entwurf',
    offered: 'Angebot',
    accepted: 'Angenommen',
    declined: 'Abgelehnt',
    expired: 'Abgelaufen',
    withdrawn: 'Zurückgezogen',
    in_force: 'In Kraft',
    revoked: 'Widerrufen',
    superseded: 'Ersetzt',
    terminated: 'Gekündigt',
    ended: 'Beendet',
};
/**
 * The transitions of a version (STBV-E B.2); the database holds the same table in a trigger.
 * An engagement follows its newest version - and after a declined, expired or withdrawn offer
 * the firm may offer anew: a new draft (STBV-LZ-06).
 */
export const VERSION_TRANSITIONS = {
    draft: ['offered'],
    offered: ['accepted', 'declined', 'expired', 'withdrawn'],
    accepted: ['in_force', 'revoked'],
    in_force: ['superseded', 'revoked', 'terminated'],
    terminated: ['ended'],
    declined: [],
    expired: [],
    withdrawn: [],
    revoked: [],
    superseded: [],
    ended: [],
};
/** Whether a version may go from one state to the other. */
export const versionMayMove = (from, to) => VERSION_TRANSITIONS[from].includes(to);
/** States after which the firm may draft a new offer for the same engagement. */
export const REOFFERABLE = ['declined', 'expired', 'withdrawn'];
export const BookingInterval = z.enum(['month', 'quarter', 'year']).meta({ id: 'BookingInterval' });
export const bookingIntervalLabel = {
    month: 'Monat',
    quarter: 'Quartal',
    year: 'Jahr',
};
export const AnnualAccountsKind = z
    .enum(['balance_sheet', 'eur', 'both'])
    .meta({ id: 'AnnualAccountsKind' });
export const annualAccountsKindLabel = {
    balance_sheet: 'Jahresabschluss (Bilanz)',
    eur: 'Einnahmenüberschussrechnung',
    both: 'Bilanz und Einnahmenüberschussrechnung',
};
export const PayrollPriceModel = z
    .enum(['tiered', 'per_payslip', 'fixed_conditional'])
    .meta({ id: 'PayrollPriceModel' });
export const engagementOptionLabel = {
    usesDatevOnline: 'DATEV Unternehmen online',
    permanentExtension: 'Dauerfristverlängerung',
    keepsCash: 'Der Mandant führt eine Kasse',
    onlineCashBook: 'Kassenbuch online',
    bankOnline: 'Zahlungsverkehr über Bank online',
    assignsReceiptsOnline: 'Der Mandant ordnet Belege online zu',
    pickupService: 'Abhol- und Bringservice für Papierbelege',
    payrollReportsOnPaper: 'Lohnauswertungen auf Papier',
    payrollReportsShipped: 'Lohnauswertungen werden versandt',
    signOffOnPaper: 'Freizeichnung des Abschlusses auf Papier',
    firmScansReceipts: 'Die Kanzlei digitalisiert Papierbelege',
    firmScansAnnualReceipts: 'Die Kanzlei scannt die Belege für den Abschluss',
    taxNoticeReceiptAuthorised: 'Empfangsvollmacht für Steuerbescheide',
    consumerRequestsEarlyStart: 'Beginn vor Ende der Widerrufsfrist ausdrücklich verlangt',
    clientAccountsOnBalanceSheet: 'Der Mandant bilanziert',
    thirdPartyData: 'Daten Dritter werden verarbeitet (Arbeitnehmer, Ehegatten, Gesellschafter)',
    sensitiveDataUnencrypted: 'Sensible Daten dürfen auch unverschlüsselt versandt werden',
    excludesMessenger: 'Keine Korrespondenz per SMS oder Messenger',
    excludesEmail: 'Keine Korrespondenz per E-Mail',
    excludesFax: 'Keine Korrespondenz per Telefax',
    generalPowerOfAttorney: 'Allgemeine Vertretungsvollmacht',
    taxPowerOfAttorney: 'Vollmacht in Steuersachen, auch ohne Steuererklärungen',
    moneyReceiptAuthorised: 'Geldempfangsvollmacht (Erstattungen, Wertsachen)',
    taxPoaRestricted: 'Vollmacht in Steuersachen mit Ausnahmen',
    taxPoaLimitedInTime: 'Vollmacht in Steuersachen zeitlich begrenzt',
    taxDataRetrieval: 'Abruf steuerlicher Daten (vorausgefüllte Steuererklärung)',
    taxSubPower: 'Untervollmacht in Steuersachen',
    disclosureToThirdParties: 'Entbindung von der Verschwiegenheit gegenüber Dritten',
    thirdPartyExperts: 'Zustimmung zur Hinzuziehung fachkundiger Dritter',
    usesBillingAgency: 'Abtretung an eine Verrechnungsstelle',
};
/**
 * The terms of the communication agreement and the declarations (KOMM, VOLLM, VOLLM-ST, ENTB):
 * shown apart from the terms of the services.
 */
export const DECLARATION_OPTIONS = [
    'sensitiveDataUnencrypted',
    'excludesMessenger',
    'excludesEmail',
    'excludesFax',
    'generalPowerOfAttorney',
    'taxPowerOfAttorney',
    'moneyReceiptAuthorised',
    'taxPoaRestricted',
    'taxPoaLimitedInTime',
    'taxDataRetrieval',
    'taxSubPower',
    'disclosureToThirdParties',
    'thirdPartyExperts',
    'usesBillingAgency',
];
export const emailProtectionLabel = {
    firm_encryption: 'Verschlüsselungsverfahren der Kanzlei',
    password: 'Passwortschutz der Anhänge',
    transport_only: 'Nur Transportverschlüsselung (ausdrücklich gewünscht)',
};
export const postModeLabel = {
    unrestricted: 'Uneingeschränkt',
    personal_only: 'Nur mit Zusatz „Persönlich/Vertraulich“',
    none: 'Keine Postsendungen',
};
const options = Object.fromEntries(ENGAGEMENT_OPTIONS.map((o) => [o, z.boolean()]));
/** A value the firm types for a document (a `{{placeholder}}` Kanos cannot derive). */
const FieldName = z
    .string()
    .regex(/^[a-z][a-z0-9_]*(\.[a-z][a-z0-9_]*)*$/, 'Fields look like ber.tax.goals')
    .max(80);
/**
 * A position the contract holds: its key in the firm's catalogue and, only where this contract
 * differs from the catalogue, a price of its own (`null`: the catalogue's price).
 */
export const EngagementPosition = z
    .object({ key: PositionKey, price: Price.nullable() })
    .strict()
    .meta({ id: 'EngagementPosition' });
const unique = (values) => new Set(values).size === values.length;
/** How often the price of a service the firm describes itself is due. */
export const CustomServicePeriod = PricePeriod.exclude(['booking_period']).meta({
    id: 'CustomServicePeriod',
});
/**
 * A service the firm describes itself for this contract, beside its catalogue (P-F-04): what it
 * is called, what it covers and its fixed price - a fee agreement in text form (§ 4 StBVV), so
 * it lands in the fee agreement. `id` keeps it apart from the others of the contract.
 */
export const CustomService = z
    .object({
    id: z.string().regex(/^[a-z0-9]{1,32}$/, 'Ids are lower-case letters and digits'),
    title: z.string().trim().min(3).max(120),
    description: z.string().trim().min(3).max(1000),
    amount: Cents.positive(),
    period: CustomServicePeriod,
})
    .strict()
    .meta({ id: 'CustomService' });
/** At most this many services of its own a contract holds. */
export const MAX_CUSTOM_SERVICES = 20;
/**
 * What a version of a contract holds (STBV-E A.4): the consumer flag with its reason, the areas
 * and their settings, the positions, the yes-or-no terms, the liability cap and the binding of
 * the offer - and the values the firm types for the documents.
 */
export const EngagementTerms = z
    .object({
    /**
     * Whether the client acts as a consumer (§ 13 BGB). Prefilled from the kind of client; a
     * value that differs from it needs a reason (STBV-C, Ableitung zu `client_is_consumer`).
     */
    clientIsConsumer: z.boolean(),
    consumerReason: z.string().trim().min(5).max(500).nullable(),
    clientLegalForm: LegalForm.nullable(),
    isNewClient: z.boolean(),
    hasPreviousAdvisor: z.boolean(),
    isFounder: z.boolean(),
    areas: z.array(CatalogArea).max(8).refine(unique, 'Each area once'),
    bookkeepingModel: BookkeepingModel.nullable(),
    bookkeepingPackage: BookkeepingPackage.nullable(),
    bookingInterval: BookingInterval.nullable(),
    serviceStart: isoDay.nullable(),
    annualAccountsKind: AnnualAccountsKind.nullable(),
    payrollPriceModel: PayrollPriceModel.nullable(),
    receiptChannel: ReceiptChannel,
    sepa: z.boolean(),
    sepaScheme: SepaScheme,
    /** E-mail protection of the communication agreement; `null`: not chosen yet. */
    emailProtection: EmailProtection.nullable(),
    /** Post to the client; `null`: not chosen yet. */
    postMode: PostMode.nullable(),
    options: z.object(options).strict(),
    positions: z
        .array(EngagementPosition)
        .max(400)
        .refine((p) => unique(p.map((x) => x.key)), 'Each position once'),
    /** Liability cap of the terms (§ 67a Abs. 1 Nr. 2 StBerG), cents; `null`: not set. */
    liabilityCap: Cents.nullable(),
    /** Until when the firm is bound to its offer (STBV-LZ-05). */
    offerValidUntil: isoDay.nullable(),
    fields: z.record(FieldName, z.string().trim().min(1).max(2000)),
    /**
     * Services the firm describes itself, beside its catalogue. Absent in versions written before
     * there were any: none (`customServicesOf`).
     */
    customServices: z
        .array(CustomService)
        .max(MAX_CUSTOM_SERVICES)
        .refine((s) => unique(s.map((x) => x.id)), 'Each service once')
        .optional(),
})
    .strict()
    .meta({ id: 'EngagementTerms' });
/** The services the firm describes itself in these terms - none in terms written before. */
export const customServicesOf = (terms) => terms.customServices ?? [];
/** Its own services stand among a version's positions under this key (`custom.<id>`). */
export const customServiceKey = (id) => `custom.${id}`;
export const isCustomServiceKey = (key) => key.startsWith('custom.');
/** The price of a service of its own: fixed, without adjustment. */
export const customServicePrice = (service) => ({
    form: 'fixed',
    amount: service.amount,
    period: service.period,
    adjustment: { kind: 'none' },
});
/** A private client is a consumer unless the contract says the services are for a business. */
export const consumerByKind = (kind) => kind === 'private';
/** What a draft holds for a question the firm may answer by its standard. */
export const termOf = (terms, question) => question === 'receiptChannel' ||
    question === 'sepa' ||
    question === 'sepaScheme' ||
    question === 'emailProtection' ||
    question === 'postMode'
    ? terms[question]
    : terms.options[question];
/**
 * Whether a question is settled by the firm's standard in this draft: the firm answers it, and the
 * draft still holds that answer. A draft that differs - begun before, or changed on purpose -
 * asks the question again rather than hide what it holds.
 */
export const settledByStandard = (questions, terms, question) => {
    const standard = standardOf(questions, question);
    return standard !== null && standard === termOf(terms, question);
};
/**
 * A new draft with the firm's standards in place of the questions it does not ask. A consumer
 * keeps the core scheme of direct debit; digital receipts drop the services for paper.
 */
export function withStandards(terms, questions) {
    const options = { ...terms.options };
    for (const [key, value] of Object.entries(questions.options)) {
        if (value !== null)
            options[key] = value;
    }
    const receiptChannel = questions.receiptChannel ?? terms.receiptChannel;
    if (receiptChannel === 'digital') {
        options.pickupService = false;
        options.firmScansReceipts = false;
    }
    return {
        ...terms,
        receiptChannel,
        sepa: questions.sepa ?? terms.sepa,
        sepaScheme: terms.clientIsConsumer ? 'core' : (questions.sepaScheme ?? terms.sepaScheme),
        emailProtection: questions.emailProtection ?? terms.emailProtection,
        postMode: questions.postMode ?? terms.postMode,
        options,
    };
}
/** A new draft, or the draft edited: from which version number (`null`: none yet). */
export const EngagementDraftInput = z
    .object({ basedOn: z.int().positive().nullable(), terms: EngagementTerms })
    .strict()
    .meta({ id: 'EngagementDraftInput' });
/** Issuing or withdrawing an offer names the version it means - a stale page changes nothing. */
export const EngagementVersionInput = z
    .object({ version: z.int().positive() })
    .strict()
    .meta({ id: 'EngagementVersionInput' });
export const EngagementDocumentParams = z
    .object({
    caseId: z.uuid(),
    document: z.string().regex(/^[A-Z]+(-[A-Z]+)?$/),
})
    .meta({ id: 'EngagementDocumentParams' });
export const EngagementVersionDocumentParams = EngagementDocumentParams.extend({
    version: z.coerce.number().int().positive(),
}).meta({ id: 'EngagementVersionDocumentParams' });
/** The number of one of the client's mandates (ADR-078), counted from 1. */
const mandateNumber = z.coerce.number().int().positive();
/** The contract of one of the client's mandates, by its number, read through an intake. */
export const MandateEngagementParams = z
    .object({ caseId: z.uuid(), mandate: mandateNumber })
    .meta({ id: 'MandateEngagementParams' });
/** A document of an offered version of one of the client's mandates' contracts. */
export const MandateEngagementDocumentParams = EngagementVersionDocumentParams.extend({
    mandate: mandateNumber,
}).meta({ id: 'MandateEngagementDocumentParams' });
//# sourceMappingURL=engagement.js.map